import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useShallow } from 'zustand/shallow';
import Filter from 'lucide-react/dist/esm/icons/filter';
import { useAmlStore } from '../../stores/amlStore';
import { Select, DatePicker } from '../../../../components/ui';
import { Button } from '../../../../components/ui';
import { useTranslation } from '../../../../hooks';
import { useUIStore } from '../../../../stores/uiStore';

const TransactionFilter: React.FC = () => {
  const { language } = useUIStore();
  const { t: tr } = useTranslation(language);

  const {
    customersForFilter,
    locations,
    transactionFilters,
    fetchCustomersForFilter,
    fetchLocations,
    setTransactionFilters,
    fetchTransactions,
  } = useAmlStore(
    useShallow((s) => ({
      customersForFilter: s.customersForFilter,
      locations: s.locations,
      transactionFilters: s.transactionFilters,
      fetchCustomersForFilter: s.fetchCustomersForFilter,
      fetchLocations: s.fetchLocations,
      setTransactionFilters: s.setTransactionFilters,
      fetchTransactions: s.fetchTransactions,
    })),
  );

  const [customerId, setCustomerId] = useState(transactionFilters.customer_id ?? '');
  const [locationId, setLocationId] = useState(transactionFilters.location_id ?? '');
  const [dateFrom, setDateFrom] = useState(transactionFilters.date_from ?? '');
  const [dateTo, setDateTo] = useState(transactionFilters.date_to ?? '');

  useEffect(() => {
    fetchCustomersForFilter();
  }, [fetchCustomersForFilter]);

  useEffect(() => {
    if (customerId) {
      fetchLocations(customerId);
    } else {
      fetchLocations();
    }
  }, [customerId, fetchLocations]);

  useEffect(() => {
    setCustomerId(transactionFilters.customer_id ?? '');
    setLocationId(transactionFilters.location_id ?? '');
    setDateFrom(transactionFilters.date_from ?? '');
    setDateTo(transactionFilters.date_to ?? '');
  }, [transactionFilters]);

  const handleApply = useCallback(() => {
    setTransactionFilters({
      customer_id: customerId || undefined,
      location_id: locationId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
    fetchTransactions(1);
  }, [customerId, locationId, dateFrom, dateTo, setTransactionFilters, fetchTransactions]);

  const handleClear = useCallback(() => {
    setCustomerId('');
    setLocationId('');
    setDateFrom('');
    setDateTo('');
    setTransactionFilters({});
    fetchTransactions(1);
  }, [setTransactionFilters, fetchTransactions]);

  const handleCustomerChange = useCallback((v: string) => {
    setCustomerId(v);
    setLocationId('');
  }, []);

  const hasFilters = !!(customerId || locationId || dateFrom || dateTo);
  const hasAppliedFilters = !!(
    transactionFilters.customer_id ||
    transactionFilters.location_id ||
    transactionFilters.date_from ||
    transactionFilters.date_to
  );

  const customerOptions = useMemo(
    () => [
      { value: '', label: tr('amlPlaceholderAllCustomers') },
      ...customersForFilter.map((c) => ({ value: c.id, label: c.name })),
    ],
    [customersForFilter, tr],
  );

  const locationOptions = useMemo(() => {
    const seen = new Set<string>();
    return [
      { value: '', label: tr('amlPlaceholderAllLocations') },
      ...locations.reduce<{ value: string; label: string }[]>((acc, l) => {
        if (!seen.has(l.name)) {
          seen.add(l.name);
          acc.push({ value: l.id, label: l.name });
        }
        return acc;
      }, []),
    ];
  }, [locations, tr]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2 text-content-muted">
        <Filter size={14} />
        <span className="text-xs font-medium">{tr('amlFilterTitle')}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-44">
          <Select
            value={customerId}
            onChange={handleCustomerChange}
            options={customerOptions}
            placeholder={tr('amlPlaceholderAllCustomers')}
            size="sm"
            searchable
          />
        </div>
        <div className="w-44">
          <Select
            value={locationId}
            onChange={setLocationId}
            options={locationOptions}
            placeholder={tr('amlPlaceholderAllLocations')}
            size="sm"
            searchable
          />
        </div>
        <div className="w-44">
          <DatePicker value={dateFrom} onChange={setDateFrom} placeholder={tr('amlPlaceholderDateFrom')} size="sm" />
        </div>
        <div className="w-44">
          <DatePicker value={dateTo} onChange={setDateTo} placeholder={tr('amlPlaceholderDateTo')} size="sm" />
        </div>
        <Button variant="outline" size="md" onClick={handleApply} disabled={!hasFilters}>
          {tr('amlFilterApply')}
        </Button>
        <Button variant="secondary" size="md" onClick={handleClear} disabled={!hasAppliedFilters}>
          {tr('amlFilterClear')}
        </Button>
      </div>
    </div>
  );
};

export default TransactionFilter;
